import { FocusMonitor } from "@angular/cdk/a11y";
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { CosmosBaseEntity } from "@app/_classes/cosmos-entity";
import { pushError } from "@app/_helpers/globalErrorHandler";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { hasUserRoleManager } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import { resolveTagDisplayColor } from "@app/_helpers/tag-color";
import { isTimeLockedForDate, isTimeLockedForDateFuture } from "@app/_helpers/timeLock";
import {
	createRxValue,
	DEFAULT_PERMISSION_GROUPS,
	distinctUntilChangedJson,
	fromRxValue,
	hasChange,
	hasPermission,
} from "@app/_helpers/utils";
import { findTimezoneByName } from "@app/account-profile/custom-timezones";
import { AppService } from "@app/app.service";
import { parseDuration } from "@app/components/duration-input-control/duration-input-utils";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "@app/components/user-single-picker-dialog/user-single-picker-dialog.component";
import { ReportsService } from "@app/pages/reports-page/reports.service";
import {
	ClientProjectPickerStepperData,
	ClientProjectPickerStepperDialogComponent,
} from "@app/shared/dialogs/client-project-picker-stepper-dialog/client-project-picker-stepper-dialog.component";
import { TagPickerDialogComponent } from "@app/shared/dialogs/tag-picker-dialog/tag-picker-dialog.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	Logger,
	MyTimesQuery,
	MyTimesService,
	Project,
	ProjectsQuery,
	TagType,
	Task,
	Time,
	UserSettingsQuery,
} from "@timeghost/web-api";
import addSeconds from "date-fns/addSeconds";
import { intervalToDuration, isValid } from "date-fns/esm";
import produce from "immer";
import { combineLatest } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, share, skip, startWith, switchMap } from "rxjs/operators";
import { firstBy } from "thenby";

const log = new Logger("TimeEntryComponent");
@UntilDestroy()
@Component({
	selector: "tg-dashboard-time-entry",
	templateUrl: "./time-entry.component.html",
	styleUrls: ["./time-entry.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		disabled: "disabled",
	},
})
export class TimeEntryComponent implements OnInit {
	readonly group = new FormGroup({
		name: new FormControl<Time["name"]>(null),
		project: new FormControl<Time["project"]>(null),
		task: new FormControl<Time["task"]>(null),
		billable: new FormControl(false),
		tags: new FormControl<Time["tags"]>([]),
		user: new FormControl<Time["user"]>(null),
		start: new FormControl<Time["start"]>(null),
		end: new FormControl<Time["end"]>(null),
		timeDiff: new FormControl<Time["timeDiff"]>(null),
	});
	get groupValue() {
		return this.group.getRawValue();
	}
	readonly group$ = fromRxValue(this.group.valueChanges);
	constructor(
		private projectsQuery: ProjectsQuery,
		private myTimesQuery: MyTimesQuery,
		private userSettingsQuery: UserSettingsQuery,
		private dialog: MatDialog,
		private myTimesService: MyTimesService,
		private appService: AppService,
		private fm: FocusMonitor,
		private elRef: ElementRef,
		private viewRef: ViewContainerRef,
		@Optional() private parent?: ReportsService
	) {}
	get disabled() {
		return this.group.disabled;
	}
	@Output()
	disabledChange = new EventEmitter();
	@Input()
	set disabled(val: boolean) {
		if (val !== this.group.disabled) this.group[val ? "disable" : "enable"]();
		this.disabledChange.emit(val);
	}
	private _checked: boolean = false;
	public get checked(): boolean {
		return this._checked;
	}
	@Output()
	checkedChange = new EventEmitter();
	@Input()
	public set checked(v: boolean) {
		if (this._checked === v) return;
		this._checked = v;
		this.checkedChange.emit(v);
	}
	private _entry = createRxValue<Time>(null);
	readonly entry$ = this._entry.asObservable().pipe(
		// only compare the mapped changeable fields
		distinctUntilChangedJson((x) => this._mapper(x))
	);
	readonly time$ = combineLatest([this.entry$, this.userSettingsQuery.select()]).pipe(
		map(([x, user]) => {
			let start = new Date(x.start),
				end = new Date(x.end);
			if (!isValid(start)) start = null;
			if (!isValid(end)) end = null;
			const tz = x.timeZone ? findTimezoneByName(x.timeZone) : x.timeZone;
			return {
				start,
				end,
				...((d) => {
					if (!d.viewStart || !d.viewEnd) return {};
					return {
						viewStart: new Date(d.viewStart),
						viewEnd: new Date(d.viewEnd),
					};
				})(x as any),
				// @ts-ignore
				timeZone: x.timeZone,
				// @ts-ignore
				inputMode: x.inputMode,
				tz,
			};
		})
	);
	readonly entry$duration = this.entry$.pipe(
		map((x) => {
			// @ts-ignore
			const inputMode: "range" | "duration" = x.inputMode;
			const start = new Date(x.start),
				end =
					inputMode === "range"
						? x.end
							? new Date(x.end)
							: new Date()
						: addSeconds(start.getTime(), ~~x.timeDiff);
			if (inputMode === "duration" && x.timeDiff >= 0) {
				const [hours, minutes] = parseDuration(x.timeDiff / 60);
				return {
					hours,
					minutes,
					seconds: 0,
				};
			}
			const { hours, minutes, seconds } = intervalToDuration({ start, end });
			return {
				hours,
				minutes,
				seconds,
			};
		})
	);
	get entry() {
		return this._entry.value;
	}
	@Output()
	entryChange = new EventEmitter();
	@Input()
	public set entry(v: Time) {
		this._entry.update(v);
		if (this.group.pristine) this.refreshGroup(v);
	}
	readonly canEdit$ = createRxValue(true);
	private _showBulk: boolean;
	public get showBulk(): boolean {
		return this._showBulk;
	}
	@Input()
	public set showBulk(v: boolean) {
		this._showBulk = v;
		this.disabled = !v;
	}
	trackId(i: number, { id }: { id: string }) {
		return id;
	}
	disableControl(controls: string[], disable: boolean) {
		controls.forEach((x) => this.group.controls[x]?.[disable ? "disable" : "enable"]());
	}

	@Input()
	showAmount: boolean = false;

	@Output()
	dateClick = new EventEmitter();

	private _loading = createRxValue(false);
	@Input()
	public set loading(v: boolean) {
		this._loading.next(v);
		this.disabled = v;
	}
	get loading() {
		return this._loading.value;
	}
	public get isLoading(): boolean {
		return this.loading;
	}
	public set isLoading(v: boolean) {
		this.loading = v;
	}
	readonly _outsideFocus = createRxValue(false);
	get outsideFocus() {
		return this._outsideFocus.value;
	}
	set outsideFocus(v: boolean) {
		this._outsideFocus.next(v);
	}
	private _mapper = ({
		billable,
		name,
		project,
		tags,
		task,
		user,
		start,
		end,
		timeDiff,
	}: typeof this.group.value = {}) => {
		return {
			billable: billable ?? null,
			name: name ?? null,
			pid: project?.id ?? null,
			tid: task?.id ?? null,
			uid: user?.id ?? null,
			tags: tags?.map(({ id }) => id).sort() ?? null,
			start: start ?? null,
			end: end ?? null,
			timeDiff: timeDiff ?? null,
		} as any;
	};
	refreshGroup(time: Time & Partial<{ checked: boolean }>) {
		this.group.patchValue(
			{
				name: time.name,
				task: time.task,
				project: time.project,
				billable: time.billable,
				tags: time.tags,
				user: time.user,
				start: time.start,
				end: time.end,
				timeDiff: time.timeDiff,
			},
			{ emitEvent: false }
		);
		this.group.setMustChange(this.group.getRawValue(), false, this._mapper);
		const canEdit = !time.project?.completed && !!time.currentUserCanEdit;
		if (!canEdit) this.group.disable();
		this.canEdit$.value = canEdit;
		this.group.updateValueAndValidity();
	}
	readonly hostFocus = this.fm.monitor(this.viewRef.element.nativeElement, true);
	private readonly valueChange$ = this.group.valueChanges.pipe(
		map(() => this.group.getRawValue()),
		distinctUntilChangedJson(this._mapper)
	);
	isEditing = combineLatest([this.hostFocus]).pipe(
		share(),
		map(([x]) => !!x && !this.loading),
		distinctUntilChanged()
	);
	private get canInteractWithEditor(): boolean {
		return !this.disabled && !this.isLoading && this.canEdit$.value;
	}
	get bulkSelectionCheckboxDisabled(): boolean {
		return this.isLoading || !this.canEdit$.value || this.disabledProject || this.group.disabled || !this.showBulk;
	}
	private withOutsideFocus<T>(action: () => Promise<T>): Promise<T> {
		this.outsideFocus = true;
		return action().finally(() => {
			this.outsideFocus = false;
		});
	}
	private createUpdatePayload() {
		const value = this.groupValue;
		return {
			...this.entry,
			name: value.name,
			project: value.project || this.defaultProject,
			task: value.task,
			tags: value.tags ?? [],
			billable: !!value.billable,
			user: value.user || this.entry.user,
		};
	}
	private syncUpdatedTime(time: Time): void {
		if (this.myTimesQuery.hasEntity(time.id))
			produce(this.myTimesQuery.getEntity(time.id), (draft) => {
				Object.assign(draft, time);
				return draft;
			});
		this.parent?.updateProjectItem(time);
	}
	private handleUpdateError(err: unknown): void {
		this.refreshGroup(this.entry);
		pushError(err);
		pushError(
			this.userSettingsQuery.getValue().id === this.entry.user.id
				? "errors.time.user"
				: "errors.time.another-user"
		);
	}
	private async persistChanges(): Promise<void> {
		const checked = (this.entry as Time & Partial<{ checked: boolean }>)?.checked;
		this.loading = true;
		try {
			const [time] = await this.myTimesService.update(this.createUpdatePayload()).toPromise();
			this.syncUpdatedTime(time);
			this.refreshGroup({ ...time, checked });
			this.appService.notifySuccess("success.updated");
		} catch (err) {
			this.handleUpdateError(err);
		} finally {
			this.loading = false;
		}
	}
	toggleBillable() {
		this.group.markAsDirty();
		this.group.patchValue({ billable: !this.groupValue.billable });
	}
	onCheckedChange() {
		if (this.canInteractWithEditor) this.checked = !this.checked;
	}
	onDateClick(event: MouseEvent) {
		if (this.canInteractWithEditor) this.dateClick.emit(event);
	}
	onToggleBillable() {
		if (this.canInteractWithEditor) this.toggleBillable();
	}
	onOpenTagsDialog() {
		if (this.canInteractWithEditor) this.openTagsDialog();
	}
	onOpenProjectPicker() {
		if (this.canInteractWithEditor) this.openProjectPicker();
	}
	onOpenUserPicker() {
		if (!this.disabled && !this.isLoading && this.isAdmin$.value) this.openUserPicker();
	}
	tagDisplayColor(tag: any & { color?: string | null }) {
		return resolveTagDisplayColor(tag.color, tag.id, (n) => this.appService.colorScheme.getColorFromScheme(n));
	}
	ngOnInit(): void {
		this.refreshGroup(this.entry);
		combineLatest([this.valueChange$, this.hostFocus.pipe(skip(1), debounceTime(200))])
			.pipe(
				untilDestroyed(this),
				filter(([, focus]) => {
					return (
						!this.loading &&
						this.group.valid &&
						this.group.enabled &&
						!this.outsideFocus &&
						!focus &&
						this.group.dirty
					);
				})
			)
			.pipe(
				distinctUntilChangedJson(([x]) => this._mapper(x)),
				debounceTime(500)
			)
			.pipe(switchMap(() => this.persistChanges()))
			.subscribe();
		combineLatest([this.userSettingsQuery.select(), this.entry$])
			.pipe(
				untilDestroyed(this),
				map(
					([x, t]) =>
						!t.project.completed &&
						(hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, x, this.entry.project) || hasUserRoleManager(x))
				)
			)
			.subscribe((x) => {
				// this.canEdit$.update(x);
				// if (x !== this.group.enabled) this.group[x ? "enable" : "disable"]();
			});
		if (this.isEntryLocked(this.group.value.project.id, new Date(this.entry.start), new Date(this.entry.end))) {
			this.group.disable();
		}
	}
	get disabledProject() {
		return !!this.entry?.project?.completed;
	}
	readonly isAdmin$ = fromRxValue(
		this.userSettingsQuery.select().pipe(
			untilDestroyed(this),
			map((x) => !!x.workspace.users?.find((u) => u.id === x.id && u.admin))
		)
	);
	openUserPicker() {
		if (this.isLoading) return;
		const { user } = this.group.value;
		this.withOutsideFocus(() =>
			toPromise(
				this.dialog
					.open(UserSinglePickerDialogComponent, {
						restoreFocus: true,
						delayFocusTrap: true,
						data: <UserSinglePickerDialogData>{
							selected: user
								? this.userSettingsQuery.getValue()?.workspace.users?.find((x) => x.id === user.id)
								: null,
							filter: ({ selected, ...x }) => {
								const status = ((user) => parseSubscriptionAsStatus(user.workspace, user))(
									this.userSettingsQuery.getValue()
								);
								return ((x.has_license || status.isTrial) && !x.removed) || selected;
							},
						},
					})
					.afterClosed()
			)
		).then((user: any) => {
			if (user) this.patchValue({ user });
		});
	}
	openProjectPicker() {
		if (this.isLoading) return;
		const time = this.entry;
		this.withOutsideFocus(() =>
			toPromise(
				this.dialog
					.open(ClientProjectPickerStepperDialogComponent, {
						data: <ClientProjectPickerStepperData>{
							skipProjectStepper: !this.isDefaultProject(this.group.value.project.id),
							project: this.group.value.project,
							task: this.group.value.task,
							userId: time.user?.id,
						},
					})
					.afterClosed()
			)
		)
			.then((x) => x || [])
			.then(([x, t]: [Project, Task]) => {
				if (x) {
					if (isTimeLockedForDate(this.userSettingsQuery.getValue(), x, new Date(time.start))) {
						this.appService.notifyError("errors.blockProjectChange");
						return;
					}
					if (isTimeLockedForDateFuture(this.userSettingsQuery.getValue(), x, new Date(time.end))) {
						this.appService.notifyError("errors.blockProjectChangeF");
						return;
					}
					if (this.group.value.project.id != x.id) {
						this.patchValue({ project: x, task: t });
					}
					if (t) {
						this.patchValue({ task: t });
					}
				}
			});
	}
	isDefaultProject(id: string) {
		return this.projectsQuery.getCount((x) => x.id === id && x.useAsDefault) > 0;
	}
	readonly projectTooltipData$ = this.group$.asObservable().pipe(
		startWith(this.group.value),
		map(() => {
			const val = this.group.getRawValue();
			return {
				project: val.project,
				task: val.task,
				tags: val.project?.tags,
			};
		})
	);
	readonly tagTooltipData$ = this.group$.asObservable().pipe(
		startWith(this.group.value),
		map(() => {
			const val = this.group.getRawValue();
			return {
				tags: val?.tags ?? [],
			};
		})
	);
	openTagsDialog() {
		const previousTags = this.group.getRawValue().tags;
		return this.withOutsideFocus(() =>
			this.dialog
				.open(TagPickerDialogComponent, {
					data: {
						data: {
							SelectedTags: this.group.value.tags ?? [],
							type: TagType.Time,
							allowTypeModify: false,
							canToggle: true,
						},
					},
				})
				.afterClosed()
				.toPromise()
		)
			.then((tags: CosmosBaseEntity[]) => tags && tags.sort(firstBy((x) => x.id, "asc")))
			.then((newTags) => {
				if (newTags && hasChange<{ id: string }[]>(previousTags, newTags, (x) => x.map((y) => y.id).sort())) {
					this.patchValue({
						tags: newTags ?? [],
					});
				}
			});
	}
	getProjectColor(id: string) {
		return this.projectsQuery.getEntity(id)?.color;
	}
	get defaultProject() {
		return this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault, limitTo: 1 })[0];
	}
	get customFormat() {
		return this.userSettingsQuery.getValue().settings.timeFormat24h ? "HH:mm" : "hh:mm a";
	}
	patchValue(value: Partial<typeof this.group.value>) {
		this.group.patchValue(value, { emitEvent: false });
		if (this.group.pristine) this.group.markAsDirty();
		this.group.updateValueAndValidity();
	}
	isEntryLocked(projectId: string, date: Date, end: Date) {
		let project = this.projectsQuery.getAll().find((x) => x.id == projectId);
		const usersettings = this.userSettingsQuery.getValue();
		const locked =
			isTimeLockedForDate(usersettings, project, date) || isTimeLockedForDateFuture(usersettings, project, end);
		return locked;
	}
}
